import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { css, StyleDeclarationValue, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { borderColor, darkGrayFontColor, shuffleGray, titles, white } from '@Styles/colors';
import { shadowXl } from '@Styles/shadows';
import { DropdownPortalContainerContext } from '../DropdownPortalContainerContext';
import { THEME } from '../Theme/theme';

type DrawerSwipeDirection = NonNullable<React.ComponentProps<typeof DrawerPrimitive.Root>['swipeDirection']>;

type DrawerContextValue = {
	hasSnapPoints: boolean;
	modal: React.ComponentProps<typeof DrawerPrimitive.Root>['modal'];
	showSwipeHandle: boolean;
	swipeAxis: 'x' | 'y';
	swipeDirection: DrawerSwipeDirection;
};

const DrawerContext = React.createContext<DrawerContextValue | null>(null);

function useDrawer() {
	const context = React.useContext(DrawerContext);
	if (!context) {
		throw new Error('useDrawer must be used within a Drawer.');
	}
	return context;
}

function Drawer({
	modal = true,
	showSwipeHandle = false,
	snapPoints,
	swipeDirection = 'down',
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
	showSwipeHandle?: boolean;
}) {
	const hasSnapPoints = snapPoints != null && snapPoints.length > 0;
	const swipeAxis: DrawerContextValue['swipeAxis'] = swipeDirection === 'down' || swipeDirection === 'up' ? 'y' : 'x';
	const contextValue = React.useMemo(
		() => ({ hasSnapPoints, modal, showSwipeHandle, swipeAxis, swipeDirection }),
		[hasSnapPoints, modal, showSwipeHandle, swipeAxis, swipeDirection]
	);

	return (
		<DrawerContext.Provider value={contextValue}>
			<DrawerPrimitive.Root
				data-slot='drawer'
				modal={modal}
				snapPoints={snapPoints}
				swipeDirection={swipeDirection}
				{...props}
			/>
		</DrawerContext.Provider>
	);
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
	return <DrawerPrimitive.Trigger data-slot='drawer-trigger' {...props} />;
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
	return <DrawerPrimitive.Portal data-slot='drawer-portal' {...props} />;
}

function DrawerClose({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Close>) {
	return <DrawerPrimitive.Close data-slot='drawer-close' {...props} />;
}

function DrawerOverlay({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof DrawerPrimitive.Backdrop>>) {
	const { hasSnapPoints } = useDrawer();
	return (
		<DrawerPrimitive.Backdrop
			data-slot='drawer-overlay'
			className={css(s.overlay, hasSnapPoints && s.overlaySnapPoints, ...styles)}
			{...props}
		/>
	);
}

const DrawerSwipeHandle = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ styles = [], ...props }, ref) => {
		const { swipeAxis, swipeDirection } = useDrawer();
		return (
			<div
				data-slot='drawer-swipe-handle'
				aria-hidden='true'
				ref={ref}
				className={css(
					s.swipeHandle,
					swipeAxis === 'y' ? s.swipeHandleAxisY : s.swipeHandleAxisX,
					swipeDirection === 'down' && s.swipeHandleDown,
					swipeDirection === 'up' && s.swipeHandleUp,
					swipeDirection === 'left' && s.swipeHandleLeft,
					swipeDirection === 'right' && s.swipeHandleRight,
					...styles
				)}
				{...props}
			/>
		);
	}
);

DrawerSwipeHandle.displayName = 'DrawerSwipeHandle';

function DrawerContent({
	styles = [],
	children,
	keepMounted = false,
	overlayStyles = [],
	...props
}: AphroditeStyled<
	React.ComponentProps<typeof DrawerPrimitive.Popup> & {
		keepMounted?: boolean;
		overlayStyles?: StyleDeclarationValue[];
	}
>) {
	const { hasSnapPoints, modal, showSwipeHandle, swipeAxis, swipeDirection } = useDrawer();
	const popupRef = React.useRef<HTMLDivElement>(null);

	return (
		<DrawerPortal keepMounted={keepMounted}>
			{modal === true && <DrawerOverlay styles={overlayStyles} />}
			<DrawerPrimitive.Viewport
				data-slot='drawer-viewport'
				className={css(s.viewport, modal === true && s.viewportModal)}
			>
				<DrawerPrimitive.Popup
					ref={popupRef}
					data-slot='drawer-popup'
					className={css(
						s.popup,
						swipeAxis === 'y' ? s.popupAxisY : s.popupAxisX,
						swipeAxis === 'y' && hasSnapPoints && s.popupSnapPointsY,
						swipeDirection === 'down' && s.popupDown,
						swipeDirection === 'up' && s.popupUp,
						swipeDirection === 'left' && s.popupLeft,
						swipeDirection === 'right' && s.popupRight,
						...styles
					)}
					{...props}
				>
					<DropdownPortalContainerContext.Provider value={popupRef}>
						{showSwipeHandle && <DrawerSwipeHandle />}
						<DrawerPrimitive.Content data-slot='drawer-content' className={css(s.content)}>
							{children}
						</DrawerPrimitive.Content>
					</DropdownPortalContainerContext.Provider>
				</DrawerPrimitive.Popup>
			</DrawerPrimitive.Viewport>
		</DrawerPortal>
	);
}

const DrawerHeader = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ styles = [], ...props }, ref) => {
		const context = React.useContext(DrawerContext);
		return (
			<div
				data-slot='drawer-header'
				ref={ref}
				className={css(s.header, context?.swipeAxis === 'y' && s.headerAxisY, ...styles)}
				{...props}
			/>
		);
	}
);

DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ styles = [], ...props }, ref) => {
		return <div data-slot='drawer-footer' ref={ref} className={css(s.footer, ...styles)} {...props} />;
	}
);

DrawerFooter.displayName = 'DrawerFooter';

function DrawerTitle({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<typeof DrawerPrimitive.Title>>) {
	return <DrawerPrimitive.Title data-slot='drawer-title' className={css(s.title, ...styles)} {...props} />;
}

function DrawerDescription({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof DrawerPrimitive.Description>>) {
	return (
		<DrawerPrimitive.Description data-slot='drawer-description' className={css(s.description, ...styles)} {...props} />
	);
}

export {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	DrawerPortal,
	DrawerSwipeHandle,
	DrawerTitle,
	DrawerTrigger,
};

// Base UI drives the swipe/stack CSS variables (--drawer-height, --drawer-swipe-*, --drawer-snap-point-offset,
// --drawer-frontmost-height, --nested-drawers) via inline styles. Only read them here — declaring any of them in
// these classes would override the live inline values, because Aphrodite emits every declaration with !important.
const s = StyleSheet.create({
	overlay: {
		position: 'fixed',
		inset: 0,
		zIndex: 50,
		minHeight: '100dvh',
		backgroundColor: 'rgba(0, 0, 0, 0.3)',
		backdropFilter: 'blur(4px)',
		opacity: 'max(var(--drawer-overlay-min-opacity, 0), calc(1 - var(--drawer-swipe-progress, 0)))' as any,
		transitionProperty: 'opacity',
		transitionDuration: '450ms',
		transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
		userSelect: 'none',
		':is([data-starting-style])': {
			opacity: 0,
		},
		':is([data-ending-style])': {
			pointerEvents: 'none',
			opacity: 0,
			transitionDuration: 'calc(var(--drawer-swipe-strength, 1) * 400ms)',
		},
		':is([data-swiping])': {
			transitionDuration: '0ms',
		},
		// iOS Safari detaches fixed elements from the visual viewport during swipes
		'@supports (-webkit-touch-callout: none)': {
			position: 'absolute',
		},
	},
	overlaySnapPoints: {
		'--drawer-overlay-min-opacity': '0.5',
	},
	viewport: {
		position: 'fixed',
		inset: 0,
		zIndex: 50,
		pointerEvents: 'none',
		userSelect: 'none',
	},
	viewportModal: {
		pointerEvents: 'auto',
	},
	popup: {
		boxSizing: 'border-box',
		pointerEvents: 'auto',
		position: 'fixed',
		zIndex: 50,
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		margin: 'var(--drawer-inset, 0px)',
		height: 'var(--drawer-content-height)',
		maxHeight: 'var(--drawer-content-max-height, none)',
		width: 'var(--drawer-content-width, auto)',
		transform: 'translate3d(var(--translate-x, 0px), var(--translate-y, 0px), 0) scale(var(--stack-scale, 1))',
		transformOrigin: 'center',
		borderRadius: 12,
		border: `1px solid ${borderColor}`,
		backgroundColor: white,
		fontSize: 14,
		boxShadow: shadowXl,
		outline: 'none',
		userSelect: 'none',
		willChange: 'transform',
		transitionProperty: 'transform, height, opacity, filter',
		transitionDuration: '450ms',
		transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
		interpolateSize: 'allow-keywords',
		'--drawer-inset': '8px',
		'--drawer-bleed-background': 'transparent',
		'--drawer-content-height': 'var(--drawer-height, auto)',
		'--bleed': '48px',
		'--peek': '16px',
		'--stack-step': '0.05',
		'--stack-height': 'var(--drawer-frontmost-height, var(--drawer-height, 0px))',
		'--stack-progress': 'clamp(0, var(--drawer-swipe-progress, 0), 1)',
		'--stack-peek-offset': 'max(0px, calc((var(--nested-drawers, 0) - var(--stack-progress)) * var(--peek)))',
		'--stack-scale-base': 'max(0, calc(1 - (var(--nested-drawers, 0) * var(--stack-step))))',
		'--stack-scale': 'clamp(0, calc(var(--stack-scale-base) + (var(--stack-step) * var(--stack-progress))), 1)',
		'--stack-shrink': 'calc(1 - var(--stack-scale))',
		// Bleed covers swipe overshoot past the drawer's edge. Transparent by default because the drawer floats
		// inset from the viewport; flush drawers (--drawer-inset: 0px) should set it to the drawer background.
		':after': {
			content: '""',
			position: 'absolute',
			pointerEvents: 'none',
			backgroundColor: 'var(--drawer-bleed-background)',
		},
		':is([data-starting-style])': {
			transform: 'var(--closed-transform)',
		},
		// 0.9999 keeps the popup on its own compositor layer so the exit transform doesn't flicker
		':is([data-ending-style])': {
			transform: 'var(--closed-transform)',
			opacity: 0.9999,
			transitionDuration: 'calc(var(--drawer-swipe-strength, 1) * 400ms)',
		},
		':is([data-swiping])': {
			transitionDuration: '0ms',
		},
		':is([data-swiping][data-ending-style])': {
			transitionDuration: 'calc(var(--drawer-swipe-strength, 1) * 400ms)',
		},
		':is([data-nested-drawer-swiping])': {
			transitionDuration: '0ms',
		},
		':is([data-nested-drawer-swiping][data-ending-style])': {
			transitionDuration: 'calc(var(--drawer-swipe-strength, 1) * 400ms)',
		},
		':is([data-nested-drawer-open])': {
			overflow: 'hidden',
			filter: 'brightness(0.95)',
		},
		':is([data-nested-drawer-open]) [data-slot="drawer-content"]': {
			opacity: 0,
		},
		':is([data-nested-drawer-swiping]) [data-slot="drawer-content"]': {
			opacity: 1,
		},
		':is([data-swiping]) [data-slot="drawer-content"]': {
			userSelect: 'none',
		},
		':is([data-nested-drawer-open]) [data-slot="drawer-swipe-handle"]': {
			opacity: 0,
		},
		':is([data-nested-drawer-swiping]) [data-slot="drawer-swipe-handle"]': {
			opacity: 1,
		},
	},
	popupAxisY: {
		left: 0,
		right: 0,
		'--drawer-content-max-height': 'calc(100dvh - 96px)',
		':after': {
			left: 0,
			right: 0,
			height: 'var(--bleed)',
		},
		':is([data-nested-drawer-open])': {
			height: 'var(--stack-height)',
		},
	},
	popupAxisX: {
		top: 0,
		bottom: 0,
		flexDirection: 'row',
		'--drawer-content-width': '75%',
		[`@media (min-width: ${THEME.breakpoints.sm}px)`]: {
			'--drawer-content-width': '384px',
		},
		':after': {
			top: 0,
			bottom: 0,
			width: 'var(--bleed)',
		},
	},
	popupSnapPointsY: {
		'--drawer-content-height': '100dvh',
	},
	popupDown: {
		bottom: 0,
		transformOrigin: 'bottom',
		'--closed-transform': 'translate3d(0, calc(100% + var(--drawer-inset, 0px) + 2px), 0)',
		'--translate-y':
			'calc(var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px) - var(--stack-peek-offset) - (var(--stack-shrink) * var(--stack-height)))',
		':after': {
			top: '100%',
		},
		':is([data-nested-drawer-open])': {
			boxShadow: '0 -20px 25px -5px rgb(0 0 0 / 0.1), 0 -8px 10px -6px rgb(0 0 0 / 0.1)',
		},
	},
	popupUp: {
		top: 0,
		transformOrigin: 'top',
		'--closed-transform': 'translate3d(0, calc(-100% - var(--drawer-inset, 0px) - 2px), 0)',
		'--translate-y':
			'calc(var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px) + var(--stack-peek-offset) + (var(--stack-shrink) * var(--stack-height)))',
		':after': {
			bottom: '100%',
		},
	},
	popupLeft: {
		left: 0,
		transformOrigin: 'left',
		'--closed-transform': 'translate3d(calc(-100% - var(--drawer-inset, 0px) - 2px), 0, 0)',
		'--translate-x':
			'calc(var(--drawer-swipe-movement-x, 0px) + var(--stack-peek-offset) + (var(--stack-shrink) * 100%))',
		':after': {
			right: '100%',
		},
	},
	popupRight: {
		right: 0,
		transformOrigin: 'right',
		'--closed-transform': 'translate3d(calc(100% + var(--drawer-inset, 0px) + 2px), 0, 0)',
		'--translate-x':
			'calc(var(--drawer-swipe-movement-x, 0px) - var(--stack-peek-offset) - (var(--stack-shrink) * 100%))',
		':after': {
			left: '100%',
		},
	},
	content: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
		overscrollBehavior: 'contain',
		borderRadius: 'inherit',
		transitionProperty: 'opacity',
		transitionDuration: '300ms',
		transitionTimingFunction: 'cubic-bezier(0.45, 1.005, 0, 1.005)',
		userSelect: 'text',
	},
	swipeHandle: {
		boxSizing: 'border-box',
		position: 'relative',
		zIndex: 10,
		display: 'flex',
		flexShrink: 0,
		cursor: 'grab',
		transitionProperty: 'opacity',
		transitionDuration: '200ms',
		':active': {
			cursor: 'grabbing',
		},
		':after': {
			content: '""',
			display: 'block',
			flexShrink: 0,
			borderRadius: 9999,
			backgroundColor: shuffleGray,
		},
	},
	swipeHandleAxisY: {
		height: 12,
		width: '100%',
		justifyContent: 'center',
		':after': {
			height: 6,
			width: 100,
		},
	},
	swipeHandleAxisX: {
		height: '100%',
		width: 12,
		alignItems: 'center',
		':after': {
			height: 100,
			width: 6,
		},
	},
	swipeHandleDown: {
		alignItems: 'flex-end',
	},
	swipeHandleUp: {
		order: 1,
		alignItems: 'flex-start',
	},
	swipeHandleLeft: {
		order: 1,
		justifyContent: 'flex-start',
	},
	swipeHandleRight: {
		justifyContent: 'flex-end',
	},
	header: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: 6,
		padding: '16px 16px 0',
	},
	headerAxisY: {
		textAlign: 'center',
		[`@media (min-width: ${THEME.breakpoints.md}px)`]: {
			textAlign: 'left',
		},
	},
	footer: {
		boxSizing: 'border-box',
		marginTop: 'auto',
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: 8,
		padding: '0 16px 16px',
	},
	title: {
		margin: 0,
		fontSize: 16,
		fontWeight: 600,
		color: titles,
	},
	description: {
		margin: 0,
		fontSize: 14,
		lineHeight: 1.5,
		color: darkGrayFontColor,
		textWrap: 'balance',
	},
});
